import React, { useCallback, useEffect, useContext, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import { AuthContext } from "../../../context/AuthContext";
import TextInputMaskFormik from "../../../components/UI/TextInput/TextInputMaskFormik";

const validationSchema = Yup.object().shape({
  codigo: Yup.string().default(""),
  nome: Yup.string().ensure().default("").required("Informe o Nome"),
  descricao: Yup.string()
    .default("")
    .ensure()
    .required("Informe a Descrição")
    .max(1000, "A descrição não pode ter mais de 1000 caracteres"),
  ano: Yup.string()
    .ensure()
    .default("")
    .required("Informe o Ano")
    .test("length", "Deve ser informado o ano com 4 dígitos", (val) => val.trim().length === 4),
  // .max(4, "Deve ser informado o ano com 4 dígitos")
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const FormCiclo = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: submitRequest, data: submitData, errors: submitErrors, isLoading: submitIsLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.codigo) submitRequest(apiRoutes.fiscalizacao.parametrizacao.alteraCiclo, values);
      else submitRequest(apiRoutes.fiscalizacao.parametrizacao.incluiCiclo, values);
    },
    [submitRequest],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { values, setSubmitting, setValues } = formik;

  useEffect(() => {
    if (submitIsLoading) return;

    if (submitData || submitErrors) setSubmitting(false);

    if (submitData) {
      toast.success("Ciclo salvo com sucesso");
      history.push("/app/ciclo");
    }
  }, [setSubmitting, submitData, submitErrors, submitIsLoading, history]);

  // Carregar item
  const { request: cicloRequest, data: cicloData, errors: cicloErrors, isLoading: cicloIsLoading } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      setSubmitting(true);
      cicloRequest(apiRoutes.fiscalizacao.parametrizacao.consultaCiclo, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [cicloRequest, setSubmitting, state]);

  useEffect(() => {
    if (cicloIsLoading) return;

    setSubmitting(false);

    if (cicloErrors) {
      toast.error(cicloErrors.message);
    }

    if (!values.codigo && cicloData && cicloData.result.length) {
      const info = {
        codigo: cicloData?.result[0]?.codigo,
        data: {
          documentoInclusao: cicloData?.result[0]?.documentoInclusao,
          nomeFormalInclusao: cicloData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: cicloData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: cicloData?.result[0]?.documentoAlteracao,
          dataInclusao: cicloData?.result[0]?.dataInclusao,
          dataAlteracao: cicloData?.result[0]?.dataAlteracao,
        },
      };

      setValues({ ...cicloData.result[0] });
      setDadosInfo(info);
    }
  }, [values.codigo, setValues, setSubmitting, cicloData, cicloErrors, cicloIsLoading]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Ciclos</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.codigo ? "Alteração de Ciclo" : "Novo Ciclo"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="codigo" />
                </Col>
              </Row>

              {/* <Button href="#" className="btn btn-primary btn-sm btn-user">Botão</Button> */}
            </div>

            <div className="card-body">
              <FormAlert
                formik={formik}
                data={submitData}
                errors={submitErrors}
                isLoading={submitIsLoading || cicloIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.codigo
                      ? !isAllowed(funcionalidades.ciclo.alterar)
                      : !isAllowed(funcionalidades.ciclo.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} sm={12} md={4}>
                      <Form.Label>Nome</Form.Label>
                      <TextInputFormik formik={formik} field="nome" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={6}>
                      <Form.Label>Descrição</Form.Label>
                      <TextInputFormik formik={formik} field="descricao" maxLength="1000" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={2}>
                      <Form.Label>Ano</Form.Label>
                      <TextInputMaskFormik
                        formik={formik}
                        field="ano"
                        format="9999"
                        placeholder="YYYY"
                        mask="9999"
                        maskChar=" "
                      />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando ciclo..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.codigo ? funcionalidades.ciclo.alterar : funcionalidades.ciclo.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      >
                        Salvar
                      </ButtonSubmitFormikPermission>
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.codigo && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormCiclo;
